import { useEffect, useState } from "react";
import React from "react";
import { Nullable } from "sonobello.utilities.react.mui";

import { SessionContextProps } from "../../../../Context/SessionContext";
import { UserContextProps } from "../../../../Context/UserContext";
import { CustomDimension } from "../../../../Models/CustomDimension";
import { LeadResult } from "../../../../Models/Lead";
import { LeadExternalSystem } from "../../../../Utils/Enums";
import { Error } from "../../../../Utils/Errors";
import { useApi } from "../../../../Utils/useApi";
import EnvironmentConfiguration from "../../../Constants/EnvironmentConfiguration";
import LeadResultType from "../../../Constants/LeadResultType";
import SessionContextValueProvider from "../../../Providers/Value/SessionContextValueProvider";
import UserContextValueProvider from "../../../Providers/Value/UserContextValueProvider";
import DispositionModal from "../Components/ConfirmationModal/DispositionModal";
import { IDispositionStepConfirmationModal } from "../Components/DispositionStep";
import SessionContextMedicalEscalationConfirmationForm from "./SessionContextMedicalEscalationConfirmationForm";
import SessionContextNotReachedConfirmationForm from "./SessionContextNotReachedConfirmationForm";
import UserContextOtherProceduresConfirmationForm from "./UserContextOtherProceduresConfirmationForm";

/** The confirmation form to be used when the lead result type is specifically paired with a particular form component. */
const ConfirmationFormMap = {
  [EnvironmentConfiguration.leadResultIds[LeadResultType.MedicalEscalation]]:
    SessionContextMedicalEscalationConfirmationForm,
  [EnvironmentConfiguration.leadResultIds[LeadResultType.NotReached]]: SessionContextNotReachedConfirmationForm,
  [EnvironmentConfiguration.leadResultIds[LeadResultType.OtherProcedures]]: UserContextOtherProceduresConfirmationForm
};

export interface ISessionContextDispositionModal
  extends IDispositionStepConfirmationModal,
    Pick<
      SessionContextProps,
      "customer" | "ignoreNonCan" | "lead" | "leadResult" | "setLeadResult" | "setCustomerInfo"
    >,
    Pick<UserContextProps, "leadResultTypes" | "ivrState"> {
  /** The sessions newly created appointment if any. */
  appointment: SessionContextProps["appointment"];
  /** The reasons for which the current session's guest is not a candidate for our services. */
  disqualificationReasons: SessionContextProps["leadDisqualifiers"];
  /** The customer that was selected during the Search step. */
  selectedCustomer: SessionContextProps["selectedCustomer"];
  /** The id of the user. */
  userId: UserContextProps["user"]["id"];
  /** The callback which ends the current session when executed. */
  endSession: SessionContextProps["helpers"]["endSession"];
}

const ControlledDispositionModal: React.FC<ISessionContextDispositionModal> = ({
  appointment,
  customer,
  disqualificationReasons,
  ignoreNonCan,
  ivrState,
  lead,
  leadResult,
  leadResultType,
  leadResultTypes,
  selectedCustomer,
  userId,
  endSession,
  handleConfirm,
  setCustomerInfo,
  setLeadResult,
  ...rest
}) => {
  const [confirmDisabledReason, setConfirmDisabledReason] = useState<Nullable<string>>(null);
  const [leadResultProps, setLeadResultProps] = useState<LeadResult>({
    id: leadResult?.id,
    type: leadResultType.id,
    customerId: customer.id,
    appointmentId: appointment?.id || null,
    opportunityId: customer.opportunity?.id || null,
    disqualifyingReasons: ignoreNonCan ? {} : CustomDimension.toObject(disqualificationReasons),
    notes: leadResult?.notes || null,
    notOfferedProcedures: leadResult?.notOfferedProcedures ? { ...leadResult.notOfferedProcedures } : {}
  });
  const [saveLeadResult, setSaveLeadResult] = useState<LeadResult>();

  useEffect(() => {
    setLeadResultProps({
      id: leadResult?.id,
      type: leadResultType.id,
      customerId: customer.id,
      appointmentId: appointment?.id || null,
      opportunityId: customer.opportunity?.id || null,
      disqualifyingReasons: ignoreNonCan ? {} : CustomDimension.toObject(disqualificationReasons),
      notes:
        leadResultType.id === EnvironmentConfiguration.leadResultIds[LeadResultType.OtherProcedures]
          ? leadResult?.notes || null
          : null,
      notOfferedProcedures: leadResult?.notOfferedProcedures || {}
    });
  }, [
    leadResult,
    leadResultType.id,
    customer.id,
    appointment?.id,
    customer.opportunity?.id,
    ignoreNonCan,
    disqualificationReasons
  ]);

  const deleteOpportunity =
    // we proceeded with an existing customer
    selectedCustomer &&
    // AND that customer did not NOT initially have an opportunity
    // (we created a new opportunity for the customer during the session)
    !selectedCustomer.opportunity?.id &&
    // AND we are dispositioning as existing patient
    leadResultTypes.find(lrt => lrt.id === saveLeadResult?.type)?.id ===
      EnvironmentConfiguration.leadResultIds[LeadResultType.ExistingPatient];

  const { response, error, isLoading } = useApi<LeadResult, LeadResult>({
    method: saveLeadResult?.id ? "put" : "post",
    url: `Legacy/V0/contacts/${lead.id}/result`,
    config: deleteOpportunity ? { params: { deleteOpportunity } } : undefined,
    data: saveLeadResult,
    valid: !!saveLeadResult,
    error: saveLeadResult?.id ? Error.PutLeadResult : Error.PostLeadResult
  });

  // ON PUT/POST LEAD RESULT SUCCESS
  useEffect(() => {
    if (response) {
      // save the response
      setLeadResult(response);

      // if we flagged the opportunity for deletion, clear it
      if (deleteOpportunity) {
        setCustomerInfo({
          opportunity: {
            campaign: lead.campaign,
            centerId: customer.centerId || undefined,
            createdByUserId: userId
          }
        });
        // force a second render to end the session after the opportunity is deleted on the customer.
        setTimeout(() => endSession(), 0);
      } else endSession();
    }
  }, [
    response,
    customer.centerId,
    deleteOpportunity,
    lead.campaign,
    saveLeadResult,
    selectedCustomer,
    userId,
    endSession,
    setCustomerInfo,
    setLeadResult,
    customer
  ]);

  // ON PUT/POST LEAD RESULT ERROR
  useEffect(() => {
    const leadResult = error?.response?.data?.data as LeadResult | undefined;
    if (leadResult) {
      setLeadResult(leadResult);
      handleConfirm();
      endSession();
    }
  }, [error, endSession, setLeadResult]);

  return DispositionModal({
    confirmDisabled: Boolean(confirmDisabledReason),
    confirmSpinner: isLoading,
    confirmDisabledTooltip: confirmDisabledReason || undefined,
    isLoading,
    leadResultType,
    formProps: {
      isConfirmed: Boolean(saveLeadResult),
      leadResultProps,
      leadResultType,
      formMap: ConfirmationFormMap,
      showIvrEmailWarning:
        lead.externalSystem === LeadExternalSystem.Email &&
        (ivrState.state !== "Unavailable" || ivrState.reason !== "Wrap Up"),
      summaryAddendum: null,
      setConfirmDisabledReason,
      setLeadResultProps
    },
    handleConfirm: () => setSaveLeadResult(leadResultProps),
    ...rest
  });
};

const SessionContextDispositionModal = SessionContextValueProvider({
  Component: ControlledDispositionModal,
  map: ({
    appointment,
    customer,
    ignoreNonCan,
    lead,
    leadDisqualifiers,
    leadResult,
    helpers: { endSession },
    selectedCustomer,
    setLeadResult,
    setCustomerInfo
  }) => ({
    appointment,
    customer,
    disqualificationReasons: leadDisqualifiers,
    ignoreNonCan,
    lead,
    leadResult,
    selectedCustomer,
    endSession,
    setLeadResult,
    setCustomerInfo
  })
});

const UserSessionContextDispositionModal: React.FC<IDispositionStepConfirmationModal> = UserContextValueProvider({
  Component: SessionContextDispositionModal,
  map: ({ leadResultTypes, user: { id }, ivrState }) => ({
    leadResultTypes,
    userId: id,
    ivrState
  })
});

export default UserSessionContextDispositionModal;
